import React, { useCallback, useEffect, useRef } from 'react'
import styled from '@emotion/styled'
import { Table, Tag, Button as ButtonAntd, Popconfirm } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { createSimpleStore } from 'react-simple-reducer'
import { useHistory } from 'react-router-dom'
import api from '../../api'

const STATUSES_REQUIRING_REFRESH = ['EM_PROCESSAMENTO', 'AGUARDANDO_CANCELAMENTO', 'PROCESSANDO']

const initialState = {
  notasFiscais: [] as any[],
  total: 0,
  loading: false,
  pagination: { page: 1, limit: 15 },
  loadingIds: [] as number[],
}

const NotasFiscaisStore = createSimpleStore(
  initialState,
  {
    setNotasFiscais: (state, { rows, count }) => ({
      ...state,
      notasFiscais: rows,
      total: count,
      loading: false,
    }),
    setLoading: (state, loading) => ({ ...state, loading }),
    setPagination: (state, pagination) => ({
      ...state,
      pagination: { ...state.pagination, ...pagination },
    }),
    setNotaLoading: (state, { id, isLoading }: { id: number; isLoading: boolean }) => ({
      ...state,
      loadingIds: isLoading
        ? [...state.loadingIds, id]
        : state.loadingIds.filter((loadingId) => loadingId !== id),
    }),
    updateNotaFiscal: (state, updatedNf: any) => ({
      ...state,
      notasFiscais: state.notasFiscais.map((nf) => (nf.id === updatedNf.id ? updatedNf : nf)),
    }),
  },
  {
    thunks: {
      fetchNotasFiscais(paginationOverride?: Partial<(typeof initialState)['pagination']>) {
        return async (dispatch, getState) => {
          const pagination = paginationOverride ?? getState().pagination
          dispatch(NotasFiscaisStore.actions.setLoading(true))
          try {
            const response = await api.NotasFiscais.list({
              page: pagination.page,
              limit: pagination.limit,
            })
            dispatch(NotasFiscaisStore.actions.setNotasFiscais(response))
          } catch (error) {
            console.error('Error fetching notas fiscais:', error)
            dispatch(NotasFiscaisStore.actions.setLoading(false))
          }
        }
      },

      refreshNotaFiscalStatus(notaFiscalId: number) {
        return async (dispatch) => {
          dispatch(NotasFiscaisStore.actions.setNotaLoading({ id: notaFiscalId, isLoading: true }))
          try {
            const updatedNf = await api.NotasFiscais.refreshStatus(notaFiscalId)
            dispatch(NotasFiscaisStore.actions.updateNotaFiscal(updatedNf))
          } catch (error) {
            console.error('Error refreshing nota fiscal status:', error)
          } finally {
            dispatch(
              NotasFiscaisStore.actions.setNotaLoading({ id: notaFiscalId, isLoading: false })
            )
          }
        }
      },

      cancelarNotaFiscal(notaFiscalId: number) {
        return async (dispatch) => {
          dispatch(NotasFiscaisStore.actions.setNotaLoading({ id: notaFiscalId, isLoading: true }))
          try {
            const updatedNf = await api.NotasFiscais.cancelar(notaFiscalId)
            dispatch(NotasFiscaisStore.actions.updateNotaFiscal(updatedNf))
          } catch (error) {
            console.error('Error cancelling nota fiscal:', error)
          } finally {
            dispatch(
              NotasFiscaisStore.actions.setNotaLoading({ id: notaFiscalId, isLoading: false })
            )
          }
        }
      },
    },
  }
)

export function NotasFiscaisList() {
  const init = useCallback((dispatch) => {
    dispatch(NotasFiscaisStore.thunks.fetchNotasFiscais())
  }, [])

  return (
    <NotasFiscaisStore.Provider init={init}>
      <Layout>
        <Container>
          <HeaderSection />
          <NotasFiscaisTable />
        </Container>
      </Layout>
    </NotasFiscaisStore.Provider>
  )
}

function HeaderSection() {
  const history = useHistory()

  return (
    <HeaderRow>
      <Title>Notas Fiscais</Title>
      <ButtonAntd type="primary" onClick={() => history.push('/notas-fiscais/nova')}>
        Nova Nota Fiscal
      </ButtonAntd>
    </HeaderRow>
  )
}

function NotasFiscaisTable() {
  const { notasFiscais, loading, pagination, total } = NotasFiscaisStore.useState()
  const dispatch = NotasFiscaisStore.useDispatch()

  const columns: ColumnsType<any> = [
    { title: 'ID', dataIndex: 'id', width: 80 },
    {
      title: 'Tomador',
      key: 'tomador',
      render: (_, nf) => (
        <div>
          <div>{nf.tomadorNome}</div>
          <small style={{ color: '#666' }}>{nf.tomadorCpfCnpj}</small>
        </div>
      ),
    },
    {
      title: 'Valor NF',
      dataIndex: 'valorNotaFiscal',
      width: 130,
      render: (value) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value || 0),
    },
    {
      title: 'Status',
      dataIndex: 'notaFiscalStatus',
      width: 160,
      render: (status) => {
        const colorMap = {
          EM_PROCESSAMENTO: 'blue',
          AGUARDANDO_ENVIO: 'orange',
          AGUARDANDO_CANCELAMENTO: 'orange',
          CONCLUIDO: 'green',
          EMITIDA: 'green',
          CANCELADO: 'red',
          NAO_ENVIAR: 'default',
        }
        return <Tag color={colorMap[status] || 'default'}>{status?.replace(/_/g, ' ')}</Tag>
      },
    },
    {
      title: 'Qtd Matrículas',
      dataIndex: 'matriculasCount',
      width: 130,
      align: 'center' as const,
    },
    {
      title: 'Criado em',
      dataIndex: 'createdAt',
      width: 110,
      render: (value) => (value ? new Date(value).toLocaleDateString('pt-BR') : '-'),
    },
    {
      title: 'Ações',
      key: 'actions',
      width: 180,
      render: (_, nf) => <NotaFiscalActions notaFiscal={nf} />,
    },
  ]

  const handleTableChange = (paginationEvent: any) => {
    const newPagination = { page: paginationEvent.current, limit: paginationEvent.pageSize }
    dispatch(NotasFiscaisStore.actions.setPagination(newPagination))
    dispatch(NotasFiscaisStore.thunks.fetchNotasFiscais(newPagination))
  }

  return (
    <TableWrapper>
      <Table
        columns={columns}
        dataSource={notasFiscais}
        loading={loading}
        rowKey="id"
        pagination={{
          current: pagination.page,
          pageSize: pagination.limit,
          total,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} itens`,
          pageSizeOptions: ['10', '15', '20', '50'],
        }}
        onChange={handleTableChange}
        scroll={{ x: 900 }}
      />
    </TableWrapper>
  )
}

function NotaFiscalActions({ notaFiscal }: { notaFiscal: any }) {
  const { loadingIds } = NotasFiscaisStore.useState()
  const dispatch = NotasFiscaisStore.useDispatch()
  const refreshDispatched = useRef(false)

  useEffect(() => {
    if (
      !refreshDispatched.current &&
      notaFiscal.notaFiscalExternaId &&
      STATUSES_REQUIRING_REFRESH.includes(notaFiscal.notaFiscalStatus)
    ) {
      refreshDispatched.current = true
      dispatch(NotasFiscaisStore.thunks.refreshNotaFiscalStatus(notaFiscal.id))
    }
  }, [])

  const isLoading = loadingIds.includes(notaFiscal.id)

  if (isLoading) {
    return (
      <ButtonAntd size="small" loading disabled>
        Verificando...
      </ButtonAntd>
    )
  }

  if (notaFiscal.notaFiscalStatus !== 'CONCLUIDO') return null

  const handleDownload = async () => {
    const { nonce } = await api.NotasFiscais.getNonce(notaFiscal.id)
    const url = `${import.meta.env.VITE_API_URL || 'http://localhost:3503'}/notas-fiscais/download/${notaFiscal.id}/${nonce}`
    window.open(url, '_blank')
  }

  const handleCancelar = () => {
    dispatch(NotasFiscaisStore.thunks.cancelarNotaFiscal(notaFiscal.id))
  }

  return (
    <ActionsRow>
      {notaFiscal.notaFiscalExternaId && (
        <ButtonAntd size="small" onClick={handleDownload}>
          Download PDF
        </ButtonAntd>
      )}
      <Popconfirm
        title="Cancelar nota fiscal"
        description="Tem certeza que deseja cancelar esta nota fiscal?"
        onConfirm={handleCancelar}
        okText="Sim, cancelar"
        cancelText="Não"
        okButtonProps={{ danger: true }}
      >
        <ButtonAntd size="small" danger>
          Cancelar
        </ButtonAntd>
      </Popconfirm>
    </ActionsRow>
  )
}

const Layout = styled.div`
  padding: 20px;
  background-color: whitesmoke;
  min-height: 100%;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.span`
  font-size: 22px;
  color: #494d50;
  font-weight: 600;
`

const ActionsRow = styled.div`
  display: flex;
  gap: 8px;
`

const TableWrapper = styled.div`
  background: #ffffff;
  border-radius: 10px;
  padding: 8px;
  overflow-x: auto;

  .ant-table-thead > tr > th {
    background: #ffffff;
    color: #585858;
    font-weight: normal;
    border-bottom: 1px solid #e7e3ec;
    padding: 12px 16px;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e7e3ec;
    padding: 8px 16px;
  }

  .ant-table-tbody > tr:hover > td {
    background: #f5f5f5;
  }
`
